
import { useState } from 'react';

export default function Integrations() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    hospital: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [hoveredSystem, setHoveredSystem] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setErrorMessage('');

    try {
      const response = await fetch('https://us-central1-prebedai-web.cloudfunctions.net/sendEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          type: 'integration'
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', hospital: '', message: '' });
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSubmitStatus('idle');
        }, 5000);
      } else {
        console.error('Error:', data);
        setSubmitStatus('error');
        setErrorMessage(data.message || 'Error al enviar el formulario. Por favor, inténtalo de nuevo.');
      }
    } catch (error) {
      console.error('Error:', error);
      setSubmitStatus('error');
      setErrorMessage('Error de conexión. Por favor, verifica tu conexión a internet e inténtalo de nuevo.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="integration-section" className="py-16 sm:py-24 bg-white border-t-4 sm:border-t-8 border-[#06B6D4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid lg:grid-cols-5 gap-8 sm:gap-12 items-center">
          {/* Left Content */}
          <div className="lg:col-span-2 space-y-5 sm:space-y-8">
            <h2 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-[#0A1628]" style={{ fontFamily: 'Inter, sans-serif' }}>
              ¿Integrarlo con tu HIS/ERP?
            </h2>
            <p className="text-xl sm:text-3xl text-gray-700 font-semibold">
              Lo integramos.
            </p>

            <p className="text-sm sm:text-lg text-gray-600 leading-relaxed">
              Integra PreBedAI a tu sistema. Sin duplicar datos ni cambiar tu flujo actual.
            </p>

            {/* Calendly-style CTA */}
            <div className="bg-gradient-to-br from-[#06B6D4]/10 to-[#8B5CF6]/10 rounded-xl sm:rounded-2xl p-4 sm:p-6 border-2 border-[#06B6D4]/30">
              <h4 className="text-lg sm:text-xl font-bold text-[#0A1628] mb-2 sm:mb-3">Consulta sobre Integración</h4>
              <p className="text-gray-600 mb-3 sm:mb-4 text-sm">Cuéntanos sobre tu infraestructura actual y tus necesidades de integración.</p>
              
              <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3" data-readdy-form id="integration-form">
                <input
                  type="text"
                  name="name"
                  placeholder="Tu nombre"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#6D28D9] transition-colors text-base"
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Tu email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#6D28D9] transition-colors text-base"
                />
                <input
                  type="text"
                  name="hospital"
                  placeholder="Hospital / Centro"
                  value={formData.hospital}
                  onChange={(e) => setFormData({ ...formData, hospital: e.target.value })}
                  required
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#6D28D9] transition-colors text-base"
                />
                <textarea
                  name="message"
                  placeholder="Explícanos tus necesidades de integración y dudas (opcional)"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  maxLength={500}
                  rows={3}
                  className="w-full px-3 sm:px-4 py-2.5 sm:py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:border-[#6D28D9] transition-colors resize-none text-base"
                ></textarea>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-4 sm:px-6 py-2.5 sm:py-3 bg-[#6D28D9] text-white rounded-lg font-semibold hover:bg-[#8B5CF6] transition-all duration-300 hover:-translate-y-1 hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap text-sm sm:text-base"
                >
                  <i className="ri-mail-send-line"></i>
                  <span>{isSubmitting ? 'Enviando...' : 'Enviar Consulta'}</span>
                </button>
              </form>

              {submitStatus === 'success' && (
                <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-green-800 text-center text-xs sm:text-sm animate-fade-in">
                  <div className="flex items-center justify-center gap-2">
                    <i className="ri-checkbox-circle-line"></i>
                    <span className="font-medium">¡Consulta enviada con éxito!</span>
                  </div>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-center text-xs sm:text-sm animate-fade-in">
                  <div className="flex flex-col items-center gap-1">
                    <div className="flex items-center gap-2">
                      <i className="ri-error-warning-line"></i>
                      <span className="font-medium">Error al enviar</span>
                    </div>
                    {errorMessage && (
                      <p className="text-xs">{errorMessage}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Content - Interactive Diagram */}
          <div className="lg:col-span-3">
            {/* Mobile: Dynamic Diagram with animations */}
            <div className="lg:hidden relative bg-gradient-to-br from-gray-50 to-white rounded-xl p-4 border border-gray-200 shadow-lg overflow-hidden">
              {/* Diagram container */}
              <div className="relative w-full" style={{ height: '340px' }}>
                {/* SVG connections */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 320 340" preserveAspectRatio="xMidYMid meet" style={{ zIndex: 1 }}>
                  <defs>
                    <linearGradient id="mobileGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.9"/>
                      <stop offset="50%" stopColor="#8B5CF6" stopOpacity="1"/>
                      <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.9"/>
                    </linearGradient>
                    <filter id="mobileGlow">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                      <feMerge><feMergeNode in="coloredBlur"/><feMergeNode in="SourceGraphic"/></feMerge>
                    </filter>
                  </defs>
                  {/* Top connection */}
                  <path d="M 160 170 L 160 65" fill="none" stroke="url(#mobileGradient)" strokeWidth="2" filter="url(#mobileGlow)"/>
                  <path d="M 160 170 L 160 65" fill="none" stroke="url(#mobileGradient)" strokeWidth="2" strokeDasharray="6 4" filter="url(#mobileGlow)">
                    <animate attributeName="stroke-dashoffset" from="0" to="20" dur="1.2s" repeatCount="indefinite"/>
                  </path>
                  <circle r="3" fill="#8B5CF6" filter="url(#mobileGlow)">
                    <animateMotion path="M 160 170 L 160 65" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  <circle r="2" fill="#06B6D4">
                    <animateMotion path="M 160 65 L 160 170" dur="2.5s" repeatCount="indefinite" begin="0.5s"/>
                  </circle>
                  {/* Right connection */}
                  <path d="M 160 170 L 265 170" fill="none" stroke="url(#mobileGradient)" strokeWidth="2" filter="url(#mobileGlow)"/>
                  <path d="M 160 170 L 265 170" fill="none" stroke="url(#mobileGradient)" strokeWidth="2" strokeDasharray="6 4" filter="url(#mobileGlow)">
                    <animate attributeName="stroke-dashoffset" from="0" to="20" dur="1.4s" repeatCount="indefinite" begin="0.3s"/>
                  </path>
                  <circle r="3" fill="#8B5CF6" filter="url(#mobileGlow)">
                    <animateMotion path="M 160 170 L 265 170" dur="2.2s" repeatCount="indefinite" begin="0.2s"/>
                  </circle>
                  <circle r="2" fill="#06B6D4">
                    <animateMotion path="M 265 170 L 160 170" dur="2.4s" repeatCount="indefinite" begin="0.8s"/>
                  </circle>
                  {/* Bottom connection */}
                  <path d="M 160 170 L 160 275" fill="none" stroke="url(#mobileGradient)" strokeWidth="2" filter="url(#mobileGlow)"/>
                  <path d="M 160 170 L 160 275" fill="none" stroke="url(#mobileGradient)" strokeWidth="2" strokeDasharray="6 4" filter="url(#mobileGlow)">
                    <animate attributeName="stroke-dashoffset" from="0" to="20" dur="1.3s" repeatCount="indefinite" begin="0.6s"/>
                  </path>
                  <circle r="3" fill="#8B5CF6" filter="url(#mobileGlow)">
                    <animateMotion path="M 160 170 L 160 275" dur="2.1s" repeatCount="indefinite" begin="0.4s"/>
                  </circle>
                  <circle r="2" fill="#06B6D4">
                    <animateMotion path="M 160 275 L 160 170" dur="2.3s" repeatCount="indefinite" begin="1s"/>
                  </circle>
                  {/* Left connection */}
                  <path d="M 160 170 L 55 170" fill="none" stroke="url(#mobileGradient)" strokeWidth="2" filter="url(#mobileGlow)"/>
                  <path d="M 160 170 L 55 170" fill="none" stroke="url(#mobileGradient)" strokeWidth="2" strokeDasharray="6 4" filter="url(#mobileGlow)">
                    <animate attributeName="stroke-dashoffset" from="0" to="20" dur="1.5s" repeatCount="indefinite" begin="0.9s"/>
                  </path>
                  <circle r="3" fill="#8B5CF6" filter="url(#mobileGlow)">
                    <animateMotion path="M 160 170 L 55 170" dur="2.3s" repeatCount="indefinite" begin="0.6s"/>
                  </circle>
                  <circle r="2" fill="#06B6D4">
                    <animateMotion path="M 55 170 L 160 170" dur="2.6s" repeatCount="indefinite" begin="1.2s"/>
                  </circle>
                  {/* Pulsing rings */}
                  <circle cx="160" cy="170" r="50" fill="none" stroke="#8B5CF6" strokeWidth="1" strokeOpacity="0.3">
                    <animate attributeName="r" values="50;75;50" dur="2.5s" repeatCount="indefinite"/>
                    <animate attributeName="stroke-opacity" values="0.3;0;0.3" dur="2.5s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="160" cy="170" r="50" fill="none" stroke="#06B6D4" strokeWidth="1" strokeOpacity="0.3">
                    <animate attributeName="r" values="50;80;50" dur="3s" repeatCount="indefinite" begin="0.5s"/>
                    <animate attributeName="stroke-opacity" values="0.3;0;0.3" dur="3s" repeatCount="indefinite" begin="0.5s"/>
                  </circle>
                  {/* Center node */}
                  <circle cx="160" cy="170" r="5" fill="#8B5CF6" filter="url(#mobileGlow)">
                    <animate attributeName="r" values="5;8;5" dur="1.5s" repeatCount="indefinite"/>
                  </circle>
                </svg>

                {/* Center - PreBedAI logo */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="relative">
                    <div className="absolute inset-0 bg-gradient-to-br from-[#6D28D9] via-[#8B5CF6] to-[#06B6D4] rounded-xl blur-lg opacity-40 animate-pulse"></div>
                    <div className="relative w-[72px] h-[72px] bg-gradient-to-br from-[#6D28D9] via-[#8B5CF6] to-[#06B6D4] rounded-xl flex items-center justify-center shadow-lg p-1">
                      <div className="w-full h-full bg-white rounded-lg flex items-center justify-center p-1">
                        <img src="/images/logo-prebed.png" alt="PreBedAI" className="w-12 h-12 object-contain"/>
                      </div>
                    </div>
                  </div>
                </div>

                {/* HIS / ADT - Top */}
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 z-20">
                  <div className="w-[88px] h-[88px] bg-white rounded-xl shadow-md flex flex-col items-center justify-center border-2 border-[#06B6D4] cursor-pointer hover:scale-105 transition-all">
                    <div className="w-9 h-9 bg-[#06B6D4] rounded-lg flex items-center justify-center mb-1">
                      <i className="ri-hospital-line text-white text-base"></i>
                    </div>
                    <p className="font-bold text-gray-800 text-xs">HIS / ADT</p>
                  </div>
                </div>

                {/* ERP - Right */}
                <div className="absolute top-1/2 right-0 transform -translate-y-1/2 z-20">
                  <div className="w-[88px] h-[88px] bg-white rounded-xl shadow-md flex flex-col items-center justify-center border-2 border-[#06B6D4] cursor-pointer hover:scale-105 transition-all">
                    <div className="w-9 h-9 bg-[#06B6D4] rounded-lg flex items-center justify-center mb-1">
                      <i className="ri-building-line text-white text-base"></i>
                    </div>
                    <p className="font-bold text-gray-800 text-xs">ERP</p>
                  </div>
                </div>

                {/* Laboratorio - Bottom */}
                <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-20">
                  <div className="w-[88px] h-[88px] bg-white rounded-xl shadow-md flex flex-col items-center justify-center border-2 border-[#06B6D4] cursor-pointer hover:scale-105 transition-all">
                    <div className="w-9 h-9 bg-[#06B6D4] rounded-lg flex items-center justify-center mb-1">
                      <i className="ri-flask-line text-white text-base"></i>
                    </div>
                    <p className="font-bold text-gray-800 text-xs">Laboratorio</p>
                  </div>
                </div>

                {/* Otros Sistemas - Left */}
                <div className="absolute top-1/2 left-0 transform -translate-y-1/2 z-20">
                  <div className="w-[88px] h-[88px] bg-white rounded-xl shadow-md flex flex-col items-center justify-center border-2 border-[#06B6D4] cursor-pointer hover:scale-105 transition-all">
                    <div className="w-9 h-9 bg-[#06B6D4] rounded-lg flex items-center justify-center mb-1">
                      <i className="ri-links-line text-white text-base"></i>
                    </div>
                    <p className="font-bold text-gray-800 text-xs text-center leading-tight">Otros<br/>Sistemas</p>
                  </div>
                </div>
              </div>
              
              <p className="text-center text-gray-600 mt-3 text-xs">
                <i className="ri-refresh-line mr-1 text-[#06B6D4]"></i>
                Intercambio bidireccional en tiempo real
              </p>
            </div>

            {/* Desktop: Ultra Dynamic Interactive Diagram */}
            <div className="hidden lg:block relative bg-gradient-to-br from-gray-50 to-white rounded-3xl p-12 border border-gray-200 shadow-lg overflow-visible">
              
              {/* Diagram Container */}
              <div className="relative w-full h-[550px]">
                
                {/* Advanced Connection Lines Canvas with Data Flow */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 1 }} viewBox="0 0 700 550" preserveAspectRatio="xMidYMid meet">
                  <defs>
                    {/* Advanced Gradients */}
                    <linearGradient id="connectionGradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#06B6D4" stopOpacity="0.9"/>
                      <stop offset="50%" stopColor="#8B5CF6" stopOpacity="1"/>
                      <stop offset="100%" stopColor="#06B6D4" stopOpacity="0.9"/>
                    </linearGradient>
                    
                    <linearGradient id="dataFlowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#06B6D4" stopOpacity="0">
                        <animate attributeName="offset" values="0;1" dur="2s" repeatCount="indefinite"/>
                      </stop>
                      <stop offset="50%" stopColor="#8B5CF6" stopOpacity="1">
                        <animate attributeName="offset" values="0.5;1.5" dur="2s" repeatCount="indefinite"/>
                      </stop>
                      <stop offset="100%" stopColor="#06B6D4" stopOpacity="0">
                        <animate attributeName="offset" values="1;2" dur="2s" repeatCount="indefinite"/>
                      </stop>
                    </linearGradient>
                    
                    <radialGradient id="pulseGradient">
                      <stop offset="0%" stopColor="#8B5CF6" stopOpacity="0.8"/>
                      <stop offset="100%" stopColor="#06B6D4" stopOpacity="0"/>
                    </radialGradient>
                    
                    {/* Advanced Glow Filter */}
                    <filter id="connectionGlow">
                      <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                    
                    <filter id="strongGlow">
                      <feGaussianBlur stdDeviation="8" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  {/* TO HIS - Multiple layers for depth */}
                  <path d="M 350 275 Q 350 200, 350 120" fill="none" stroke="#06B6D4" strokeWidth="4" strokeOpacity="0.15"/>
                  <path d="M 350 275 Q 350 200, 350 120" fill="none" stroke="url(#connectionGradient1)" strokeWidth="3" filter="url(#connectionGlow)"/>
                  <path d="M 350 275 Q 350 200, 350 120" fill="none" stroke="url(#connectionGradient1)" strokeWidth="3" strokeDasharray="8 6" filter="url(#strongGlow)">
                    <animate attributeName="stroke-dashoffset" from="0" to="28" dur="1.2s" repeatCount="indefinite"/>
                  </path>
                  
                  {/* Data Packets - HIS Direction */}
                  <circle r="6" fill="#8B5CF6" filter="url(#strongGlow)">
                    <animateMotion path="M 350 275 Q 350 200, 350 120" dur="2s" repeatCount="indefinite"/>
                  </circle>
                  <circle r="4" fill="#06B6D4" filter="url(#connectionGlow)">
                    <animateMotion path="M 350 120 Q 350 200, 350 275" dur="2.5s" repeatCount="indefinite" begin="0.5s"/>
                  </circle>
                  <circle r="3" fill="#FFFFFF" stroke="#8B5CF6" strokeWidth="1">
                    <animateMotion path="M 350 275 Q 350 200, 350 120" dur="1.8s" repeatCount="indefinite" begin="1s"/>
                  </circle>
                  
                  {/* TO ERP - Multiple layers */}
                  <path d="M 350 275 Q 500 275, 600 275" fill="none" stroke="#06B6D4" strokeWidth="4" strokeOpacity="0.15"/>
                  <path d="M 350 275 Q 500 275, 600 275" fill="none" stroke="url(#connectionGradient1)" strokeWidth="3" filter="url(#connectionGlow)"/>
                  <path d="M 350 275 Q 500 275, 600 275" fill="none" stroke="url(#connectionGradient1)" strokeWidth="3" strokeDasharray="8 6" filter="url(#strongGlow)">
                    <animate attributeName="stroke-dashoffset" from="0" to="28" dur="1.4s" repeatCount="indefinite" begin="0.3s"/>
                  </path>
                  
                  {/* Data Packets - ERP Direction */}
                  <circle r="6" fill="#8B5CF6" filter="url(#strongGlow)">
                    <animateMotion path="M 350 275 Q 500 275, 600 275" dur="2.2s" repeatCount="indefinite" begin="0.2s"/>
                  </circle>
                  <circle r="4" fill="#06B6D4" filter="url(#connectionGlow)">
                    <animateMotion path="M 600 275 Q 500 275, 350 275" dur="2.4s" repeatCount="indefinite" begin="0.8s"/>
                  </circle>
                  <circle r="3" fill="#FFFFFF" stroke="#06B6D4" strokeWidth="1">
                    <animateMotion path="M 350 275 Q 500 275, 600 275" dur="1.9s" repeatCount="indefinite" begin="1.3s"/>
                  </circle>
                  
                  {/* TO LAB - Multiple layers */}
                  <path d="M 350 275 Q 350 350, 350 430" fill="none" stroke="#06B6D4" strokeWidth="4" strokeOpacity="0.15"/>
                  <path d="M 350 275 Q 350 350, 350 430" fill="none" stroke="url(#connectionGradient1)" strokeWidth="3" filter="url(#connectionGlow)"/>
                  <path d="M 350 275 Q 350 350, 350 430" fill="none" stroke="url(#connectionGradient1)" strokeWidth="3" strokeDasharray="8 6" filter="url(#strongGlow)">
                    <animate attributeName="stroke-dashoffset" from="0" to="28" dur="1.3s" repeatCount="indefinite" begin="0.6s"/>
                  </path>
                  
                  {/* Data Packets - LAB Direction */}
                  <circle r="6" fill="#8B5CF6" filter="url(#strongGlow)">
                    <animateMotion path="M 350 275 Q 350 350, 350 430" dur="2.1s" repeatCount="indefinite" begin="0.4s"/>
                  </circle>
                  <circle r="4" fill="#06B6D4" filter="url(#connectionGlow)">
                    <animateMotion path="M 350 430 Q 350 350, 350 275" dur="2.3s" repeatCount="indefinite" begin="1s"/>
                  </circle>
                  <circle r="3" fill="#FFFFFF" stroke="#8B5CF6" strokeWidth="1">
                    <animateMotion path="M 350 275 Q 350 350, 350 430" dur="1.7s" repeatCount="indefinite" begin="1.5s"/>
                  </circle>
                  
                  {/* TO OTHER - Multiple layers */}
                  <path d="M 350 275 Q 200 275, 100 275" fill="none" stroke="#06B6D4" strokeWidth="4" strokeOpacity="0.15"/>
                  <path d="M 350 275 Q 200 275, 100 275" fill="none" stroke="url(#connectionGradient1)" strokeWidth="3" filter="url(#connectionGlow)"/>
                  <path d="M 350 275 Q 200 275, 100 275" fill="none" stroke="url(#connectionGradient1)" strokeWidth="3" strokeDasharray="8 6" filter="url(#strongGlow)">
                    <animate attributeName="stroke-dashoffset" from="0" to="28" dur="1.5s" repeatCount="indefinite" begin="0.9s"/>
                  </path>
                  
                  {/* Data Packets - OTHER Direction */}
                  <circle r="6" fill="#8B5CF6" filter="url(#strongGlow)">
                    <animateMotion path="M 350 275 Q 200 275, 100 275" dur="2.3s" repeatCount="indefinite" begin="0.6s"/>
                  </circle>
                  <circle r="4" fill="#06B6D4" filter="url(#connectionGlow)">
                    <animateMotion path="M 100 275 Q 200 275, 350 275" dur="2.6s" repeatCount="indefinite" begin="1.2s"/>
                  </circle>
                  <circle r="3" fill="#FFFFFF" stroke="#06B6D4" strokeWidth="1">
                    <animateMotion path="M 350 275 Q 200 275, 100 275" dur="2s" repeatCount="indefinite" begin="1.7s"/>
                  </circle>
                  
                  {/* Multiple Pulsing Rings from Center */}
                  <circle cx="350" cy="275" r="90" fill="none" stroke="#8B5CF6" strokeWidth="2" strokeOpacity="0.3" filter="url(#connectionGlow)">
                    <animate attributeName="r" values="90;130;90" dur="2.5s" repeatCount="indefinite"/>
                    <animate attributeName="stroke-opacity" values="0.4;0;0.4" dur="2.5s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="350" cy="275" r="90" fill="none" stroke="#06B6D4" strokeWidth="2" strokeOpacity="0.3" filter="url(#connectionGlow)">
                    <animate attributeName="r" values="90;140;90" dur="3s" repeatCount="indefinite" begin="0.5s"/>
                    <animate attributeName="stroke-opacity" values="0.4;0;0.4" dur="3s" repeatCount="indefinite" begin="0.5s"/>
                  </circle>
                  <circle cx="350" cy="275" r="90" fill="none" stroke="#8B5CF6" strokeWidth="2" strokeOpacity="0.3" filter="url(#connectionGlow)">
                    <animate attributeName="r" values="90;120;90" dur="2s" repeatCount="indefinite" begin="1s"/>
                    <animate attributeName="stroke-opacity" values="0.5;0;0.5" dur="2s" repeatCount="indefinite" begin="1s"/>
                  </circle>
                  
                  {/* Connection Nodes at intersections */}
                  <circle cx="350" cy="275" r="8" fill="#8B5CF6" filter="url(#strongGlow)">
                    <animate attributeName="r" values="8;12;8" dur="1.5s" repeatCount="indefinite"/>
                  </circle>
                  <circle cx="600" cy="275" r="6" fill="#06B6D4" filter="url(#connectionGlow)">
                    <animate attributeName="r" values="6;9;6" dur="1.5s" repeatCount="indefinite" begin="0.6s"/>
                  </circle>
                  <circle cx="100" cy="275" r="6" fill="#06B6D4" filter="url(#connectionGlow)">
                    <animate attributeName="r" values="6;9;6" dur="1.5s" repeatCount="indefinite" begin="1.2s"/>
                  </circle>
                </svg>

                {/* Center Logo - PreBedAI - Enhanced with pulsing effects */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <div className="relative">
                    {/* Multiple animated glow layers */}
                    <div className="absolute inset-0 bg-gradient-to-br from-[#6D28D9] via-[#8B5CF6] to-[#06B6D4] rounded-3xl blur-3xl opacity-50 scale-110 animate-pulse"></div>
                    <div className="absolute inset-0 bg-gradient-to-br from-[#06B6D4] via-[#8B5CF6] to-[#6D28D9] rounded-3xl blur-2xl opacity-30 scale-125 animate-pulse" style={{ animationDelay: '0.5s', animationDuration: '2.5s' }}></div>
                    
                    <div className="relative w-44 h-44 bg-gradient-to-br from-[#6D28D9] via-[#8B5CF6] to-[#06B6D4] rounded-3xl flex items-center justify-center shadow-2xl p-2 animate-gradient-shift">
                      <div className="w-full h-full bg-white rounded-2xl flex items-center justify-center p-4 shadow-inner">
                        <img 
                          src="/images/logo-prebed.png" 
                          alt="PreBedAI" 
                          className="w-32 h-32 object-contain"
                        />
                      </div>
                    </div>
                    
                  </div>
                </div>

                {/* HIS/ADT - Top - Enhanced with activity indicators */}
                <div 
                  className="absolute top-0 left-1/2 transform -translate-x-1/2 z-20"
                  onMouseEnter={() => setHoveredSystem('his')}
                  onMouseLeave={() => setHoveredSystem(null)}
                >
                  <div className="relative">
                    {/* Animated glow effect */}
                    {hoveredSystem === 'his' && (
                      <div className="absolute inset-0 bg-[#6D28D9] rounded-2xl blur-xl opacity-50 scale-125 animate-pulse"></div>
                    )}
                    
                    <div className={`relative w-32 h-32 bg-white rounded-2xl shadow-xl flex flex-col items-center justify-center border-2 transition-all duration-300 cursor-pointer ${
                      hoveredSystem === 'his' ? 'border-[#6D28D9] scale-110 shadow-2xl shadow-[#6D28D9]/30' : 'border-[#06B6D4] hover:border-[#8B5CF6]'
                    }`}>
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-2 transition-all duration-300 ${
                        hoveredSystem === 'his' ? 'bg-[#6D28D9] scale-110' : 'bg-[#06B6D4]'
                      }`}>
                        <i className="ri-hospital-line text-3xl text-white"></i>
                      </div>
                      <p className="font-bold text-gray-800 text-sm">HIS / ADT</p>
                      
                      {/* Data transfer arrows */}
                      <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
                        <i className="ri-arrow-down-line text-[#06B6D4] text-xs animate-bounce"></i>
                        <i className="ri-arrow-up-line text-[#8B5CF6] text-xs animate-bounce" style={{ animationDelay: '0.3s' }}></i>
                      </div>
                    </div>
                  </div>
                  
                  {hoveredSystem === 'his' && (
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-4 w-64 bg-[#0A1628] text-white text-xs rounded-xl p-4 shadow-2xl z-50 animate-fade-in">
                      <p className="font-bold mb-3 text-[#06B6D4]">Datos intercambiados</p>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-[#06B6D4]"></i>
                          <span>Altas confirmadas</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-[#8B5CF6]"></i>
                          <span>Ingresos programados</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-[#06B6D4]"></i>
                          <span>Movimientos internos</span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* ERP - Right - Enhanced with activity indicators */}
                <div 
                  className="absolute top-1/2 right-0 transform -translate-y-1/2 z-20"
                  onMouseEnter={() => setHoveredSystem('erp')}
                  onMouseLeave={() => setHoveredSystem(null)}
                >
                  <div className="relative">
                    {/* Animated glow effect */}
                    {hoveredSystem === 'erp' && (
                      <div className="absolute inset-0 bg-[#6D28D9] rounded-2xl blur-xl opacity-50 scale-125 animate-pulse"></div>
                    )}
                    
                    <div className={`relative w-32 h-32 bg-white rounded-2xl shadow-xl flex flex-col items-center justify-center border-2 transition-all duration-300 cursor-pointer ${
                      hoveredSystem === 'erp' ? 'border-[#6D28D9] scale-110 shadow-2xl shadow-[#6D28D9]/30' : 'border-[#06B6D4] hover:border-[#8B5CF6]'
                    }`}>
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-2 transition-all duration-300 ${
                        hoveredSystem === 'erp' ? 'bg-[#6D28D9] scale-110' : 'bg-[#06B6D4]'
                      }`}>
                        <i className="ri-building-line text-3xl text-white"></i>
                      </div>
                      <p className="font-bold text-gray-800 text-sm">ERP</p>
                      
                      {/* Data transfer arrows */}
                      <div className="absolute -left-3 top-1/2 -translate-y-1/2 flex flex-col gap-1">
                        <i className="ri-arrow-left-line text-[#06B6D4] text-xs animate-pulse"></i>
                        <i className="ri-arrow-right-line text-[#8B5CF6] text-xs animate-pulse" style={{ animationDelay: '0.3s' }}></i>
                      </div>
                    </div>
                  </div>
                  
                  {hoveredSystem === 'erp' && (
                    <div className="absolute top-1/2 right-full -translate-y-1/2 mr-4 w-64 bg-[#0A1628] text-white text-xs rounded-xl p-4 shadow-2xl z-50 animate-fade-in">
                      <p className="font-bold mb-3 text-[#06B6D4]">Datos intercambiados</p>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-[#06B6D4]"></i>
                          <span>Órdenes de limpieza</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-[#8B5CF6]"></i>
                          <span>Mantenimiento</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-[#06B6D4]"></i>
                          <span>Recursos disponibles</span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Laboratorio - Bottom - Enhanced with activity indicators */}
                <div 
                  className="absolute bottom-0 left-1/2 transform -translate-x-1/2 z-20"
                  onMouseEnter={() => setHoveredSystem('lab')}
                  onMouseLeave={() => setHoveredSystem(null)}
                >
                  <div className="relative">
                    {/* Animated glow effect */}
                    {hoveredSystem === 'lab' && (
                      <div className="absolute inset-0 bg-[#6D28D9] rounded-2xl blur-xl opacity-50 scale-125 animate-pulse"></div>
                    )}
                    
                    <div className={`relative w-32 h-32 bg-white rounded-2xl shadow-xl flex flex-col items-center justify-center border-2 transition-all duration-300 cursor-pointer ${
                      hoveredSystem === 'lab' ? 'border-[#6D28D9] scale-110 shadow-2xl shadow-[#6D28D9]/30' : 'border-[#06B6D4] hover:border-[#8B5CF6]'
                    }`}>
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-2 transition-all duration-300 ${
                        hoveredSystem === 'lab' ? 'bg-[#6D28D9] scale-110' : 'bg-[#06B6D4]'
                      }`}>
                        <i className="ri-flask-line text-3xl text-white"></i>
                      </div>
                      <p className="font-bold text-gray-800 text-sm">Laboratorio</p>
                      
                      {/* Data transfer arrows */}
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 flex gap-1">
                        <i className="ri-arrow-up-line text-[#06B6D4] text-xs animate-bounce"></i>
                        <i className="ri-arrow-down-line text-[#8B5CF6] text-xs animate-bounce" style={{ animationDelay: '0.3s' }}></i>
                      </div>
                    </div>
                  </div>
                  
                  {hoveredSystem === 'lab' && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-4 w-64 bg-[#0A1628] text-white text-xs rounded-xl p-4 shadow-2xl z-50 animate-fade-in">
                      <p className="font-bold mb-3 text-[#06B6D4]">Datos intercambiados</p>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-[#06B6D4]"></i>
                          <span>Resultados pendientes</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-[#8B5CF6]"></i>
                          <span>Pruebas completadas</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-red-400"></i>
                          <span>Alertas críticas</span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Otros Sistemas - Left - Enhanced with activity indicators */}
                <div 
                  className="absolute top-1/2 left-0 transform -translate-y-1/2 z-20"
                  onMouseEnter={() => setHoveredSystem('other')}
                  onMouseLeave={() => setHoveredSystem(null)}
                >
                  <div className="relative">
                    {/* Animated glow effect */}
                    {hoveredSystem === 'other' && (
                      <div className="absolute inset-0 bg-[#6D28D9] rounded-2xl blur-xl opacity-50 scale-125 animate-pulse"></div>
                    )}
                    
                    <div className={`relative w-32 h-32 bg-white rounded-2xl shadow-xl flex flex-col items-center justify-center border-2 transition-all duration-300 cursor-pointer ${
                      hoveredSystem === 'other' ? 'border-[#6D28D9] scale-110 shadow-2xl shadow-[#6D28D9]/30' : 'border-[#06B6D4] hover:border-[#8B5CF6]'
                    }`}>
                      <div className={`w-14 h-14 rounded-xl flex items-center justify-center mb-2 transition-all duration-300 ${
                        hoveredSystem === 'other' ? 'bg-[#6D28D9] scale-110' : 'bg-[#06B6D4]'
                      }`}>
                        <i className="ri-links-line text-3xl text-white"></i>
                      </div>
                      <p className="font-bold text-gray-800 text-sm text-center leading-tight">Otros<br/>Sistemas</p>
                      
                      {/* Data transfer arrows */}
                      <div className="absolute -right-3 top-1/2 -translate-y-1/2 flex flex-col gap-1">
                        <i className="ri-arrow-right-line text-[#06B6D4] text-xs animate-pulse"></i>
                        <i className="ri-arrow-left-line text-[#8B5CF6] text-xs animate-pulse" style={{ animationDelay: '0.3s' }}></i>
                      </div>
                    </div>
                  </div>
                  
                  {hoveredSystem === 'other' && (
                    <div className="absolute top-1/2 left-full -translate-y-1/2 ml-4 w-64 bg-[#0A1628] text-white text-xs rounded-xl p-4 shadow-2xl z-50 animate-fade-in">
                      <p className="font-bold mb-3 text-[#06B6D4]">Datos intercambiados</p>
                      <ul className="space-y-2">
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-[#06B6D4]"></i>
                          <span>Farmacia</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-[#8B5CF6]"></i>
                          <span>Radiología</span>
                        </li>
                        <li className="flex items-center gap-2">
                          <i className="ri-arrow-right-s-line text-[#06B6D4]"></i>
                          <span>Quirófanos</span>
                        </li>
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              {/* Explanation */}
              <p className="text-center text-gray-600 mt-4 text-sm">
                <i className="ri-refresh-line mr-2 text-[#06B6D4]"></i>
                Intercambio bidireccional en tiempo real
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
